import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession, fetchSubscriptionPlans } from '../utils/api';
import analytics from '../services/analytics';
import api from '../utils/api';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function SubscribeButtonWithCoupon({ 
  variant = 'contained', 
  size = 'medium',
  fullWidth = false,
  sx = {},
  onError = null,
  color = 'primary',
  isAlphaTester = false,
  showCouponStatus = true
}) {
  const [loading, setLoading] = useState(false);
  const [planId, setPlanId] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [loadingCoupon, setLoadingCoupon] = useState(true);
  
  // Fetch the plan ID on mount
  useEffect(() => {
    fetchSubscriptionPlans()
      .then(res => {
        const proPlan = res.data.find(plan => 
          plan.plan_type === 'premium' || 
          plan.plan_name.toLowerCase().includes('pro')
        );
        if (proPlan) {
          setPlanId(proPlan.plan_id);
        } else if (res.data.length > 0) {
          setPlanId(res.data[0].plan_id);
        }
      })
      .catch(err => {
        console.error('Failed to fetch subscription plans:', err);
      });
  }, []);

  // Fetch coupon status
  useEffect(() => {
    if (isAlphaTester && showCouponStatus) {
      api.get('/coupon/all-status')
        .then(response => {
          const foundersDeal = response.data?.coupons?.find(c => c.coupon_code === 'ALPHA90');
          setCouponData(foundersDeal);
          setLoadingCoupon(false);
        })
        .catch(err => {
          console.error('Failed to load coupon status:', err);
          setLoadingCoupon(false);
        });
    } else {
      setLoadingCoupon(false);
    }
  }, [isAlphaTester, showCouponStatus]);

  const handleCheckout = async () => {
    if (!planId) {
      console.error('No plan ID available');
      if (onError) {
        onError('Subscription plan not available. Please try again later.');
      }
      return;
    }

    setLoading(true);
    
    try {
      // Track checkout initiation
      analytics.trackCheckoutInitiated('subscribe_button', {
        plan_id: planId,
        is_alpha_tester: isAlphaTester,
        has_coupon: isAlphaTester,
        button_location: 'quick_subscribe',
        coupon_remaining: couponData?.remaining_uses
      });

      const stripe = await stripePromise;
      
      const successUrl = `${window.location.origin}/subscription/success`;
      const cancelUrl = window.location.href;
      const couponCode = isAlphaTester ? 'ALPHA90' : null;
      
      const response = await createCheckoutSession(planId, successUrl, cancelUrl, couponCode);
      
      const result = await stripe.redirectToCheckout({
        sessionId: response.data.session_id,
      });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      
      // Track checkout error
      analytics.trackError(error, {
        context: 'subscribe_button_checkout',
        plan_id: planId,
        is_alpha_tester: isAlphaTester
      });
      
      if (onError) {
        onError(error.message || 'Failed to start checkout. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getButtonContent = () => {
    if (!isAlphaTester) {
      return 'PRO Subscription';
    }

    if (loadingCoupon || !couponData) {
      return 'PRO Subscription (90% OFF)';
    }

    const total = couponData.max_uses;
    const remaining = couponData.remaining_uses;
    const spotsUsed = total - remaining;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span">PRO Subscription (90% OFF)</Typography>
        {showCouponStatus && (
          <Typography component="span" sx={{ fontSize: '0.875em', fontWeight: 'bold' }}>
            - {spotsUsed > 0 && (
              <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{total}</span>
            )}{' '}
            {remaining} left!
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Button
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      color={color}
      onClick={handleCheckout}
      disabled={loading || !planId || (couponData && !couponData.is_available)}
      sx={sx}
    >
      {loading ? (
        <>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Processing...
        </>
      ) : (
        getButtonContent()
      )}
    </Button>
  );
}

export default SubscribeButtonWithCoupon;